import { Component, OnInit, Input, HostListener } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, Validators, FormGroup, FormControl } from '@angular/forms';
import { Delivery } from 'src/classes/delivery';
import { Contact } from 'src/classes/contact';
import { Subject } from 'rxjs';
import { PBIService } from 'src/services/pbi/pbi.service';
import { interpolate, clamp } from 'src/utils/interpolate';

function getContactsFromIds(idList: number[], contacts: Contact[]): Contact[] {
  const result = [];
  for (const contact of contacts) {
    for (const idContact of idList) {
      if (contact.idContact === idContact) {
        result.push(contact);
      }
    }
  }
  return result;
}

@Component({
  selector: 'app-delivery-form-modal',
  templateUrl: './delivery-form-modal.component.html',
  styleUrls: ['./delivery-form-modal.component.scss']
})
export class DeliveryFormModalComponent implements OnInit {
  @Input() public delivery: Delivery = new Delivery();
  @Input() public contactList: Contact[] = [];
  @Input() public workspaceId: string;
  @Input() public reportId: string;
  @Input() public availableTags: {tag: string, count: number}[] = [];

  eventOpenmodal: Subject<any> = new Subject<any>();
  eventUpdateFilter: Subject<any> = new Subject<any>();

  public deliveryForm: FormGroup;
  public selectedContacts: Contact[];
  public forceFormShowError = false;
  public filterAppliedCount: number;
  public loadingFilterElement = false;
  public messageEditorHeight = this.getMessageEditorHeight();
  public rightTabView: "contacts" | "tags" = "contacts";
  public selectedTags: {tag: string, count: number}[] = [];

  get IdDelivery() { return this.deliveryForm.get('IdDelivery'); }
  get Subject() { return this.deliveryForm.get('Subject'); }
  get Message() { return this.deliveryForm.get('Message'); }
  get ReportType() { return this.deliveryForm.get('ReportType'); }
  get Contacts() { return this.deliveryForm.get('Contacts'); }
  get Tags() { return this.deliveryForm.get('Tags'); }

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private powerBiApi: PBIService
  ) { }

  ngOnInit() {
    const filterString = this.delivery.filters ? this.delivery.filters : '[]';

    const selectedTagsArray = this.extractSelectedTagArray(this.delivery);

    const selectedTags = selectedTagsArray.map(tag => {
      let count = 0;
      for (let tagObj of this.availableTags) {
        if (tagObj.tag === tag) {
          count = tagObj.count;
          break;
        }
      }
      return {
        tag,
        count
      }
    });

    this.deliveryForm = this.formBuilder.group({
      IdDelivery: [this.delivery.idDelivery ? this.delivery.idDelivery : null],
      Subject: [this.delivery.subject ? this.delivery.subject : '', [Validators.required, Validators.minLength(3)]],
      Message: [this.delivery.message ? this.delivery.message : '', [Validators.required, Validators.minLength(3)]],
      ReportType: [this.delivery.type ? this.delivery.type : 'email', Validators.required],
      Contacts: [this.delivery.contactId ? this.delivery.contactId : []],
      Tags: [selectedTags],
      Filters: [filterString]
    });
    if (filterString && filterString.length > 2) {
      const filterObject = JSON.parse(filterString);
      const activeFilters = filterObject.filter(filter => filter && (typeof filter.operator !== 'string' || filter.operator !== 'All'));
      this.filterAppliedCount = activeFilters.length;
    }
    this.selectedTags = selectedTags;
    this.selectedContacts = getContactsFromIds(this.delivery ? this.delivery.contactId : [], this.contactList);
  }

  private extractSelectedTagArray(delivery: Delivery) {
    if (!delivery || !delivery.tags) {
      return [];
    } else if (delivery.tags instanceof Array) {
      return delivery.tags;
    } else if (typeof delivery.tags === "string") {
      return delivery.tags.split(" ").filter(a => a.trim().length !== 0);
    }
    console.warn("Warning: Unknown delivery tag format / type:");
    console.log(delivery.tags);
    console.warn("Falling back to empty array");
    return [];
  }

  get isNew() {
    return !(this.delivery && this.delivery.idDelivery);
  }

  onSelectTabView(str: "contacts" | "tags") {
    this.rightTabView = str;
  }

  getMessageEditorHeight() {
    const h = window.innerHeight;

    const height = (
      (h < 635) ?
      interpolate(566, 70, 620, 100, h) :
      interpolate(670, 145, 722, 245, h)
    );

    return 100 + clamp(height, 70, 245);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    if (this.getMessageEditorHeight() !== this.messageEditorHeight) {
      this.messageEditorHeight = this.getMessageEditorHeight();
    }
  }

  onContactChange(selection: Contact[]) {
    this.selectedContacts = selection.filter(sel => sel !== null);
    this.delivery.contactId = this.selectedContacts.map(contact => contact.idContact);
    const contactInput = this.deliveryForm.get('Contacts');
    contactInput.markAsDirty();
    contactInput.markAsTouched();
    contactInput.setValue(this.delivery ? this.delivery.contactId : this.selectedContacts.map(contact => contact.idContact));
  }

  onTagChange(selection: {tag: string, count: number}[]) {
    this.selectedTags = selection;
    const tagInput = this.deliveryForm.get('Tags');
    tagInput.markAsDirty();
    tagInput.markAsTouched();
    tagInput.setValue(selection);
  }

  onCancelClick() {
    this.activeModal.close(null);
  }

  isEmptyDestination() {
    const tagValue = this.deliveryForm.get('Tags').value;
    const isTagEmpty = (tagValue instanceof Array && tagValue.length === 0) || (typeof tagValue === "string" && tagValue.trim() === "");
    const contactValue = this.deliveryForm.get('Contacts').value;
    const isContactEmpty = (contactValue instanceof Array && contactValue.length === 0) || (typeof contactValue === "string" && contactValue.trim() === "");
    return (isTagEmpty && isContactEmpty);
  }

  onSubmitClick() {
    if (this.deliveryForm.invalid || !this.deliveryForm.valid) {
      this.forceFormShowError = true;
      return;
    }
    if (this.isEmptyDestination()) {
      this.forceFormShowError = true;
      return;
    }
    this.forceFormShowError = false;
    const value = this.deliveryForm.value;
    value.Tags = (value.Tags as {tag: string, count: number}[]).map(tagObj => tagObj.tag);
    this.activeModal.close(value);
  }

  async getToken(workspaceId: string, reportId: string): Promise<any> {
    if (!workspaceId || !reportId) {
      throw new Error('Expected string (workspaceId and reportId) as parameters, got ' + (typeof workspaceId) + ', ' + (typeof reportId));
    }
    try {
      return await this.powerBiApi.getReportToken(workspaceId, reportId);
    } catch(e) {
      alert(`Error on load report data. Details ${e && e.stack ? e.stack : (e instanceof Error ? e : JSON.stringify(e))}`);
    }
    return undefined;
  }

  async onEditFilterClick() {
    this.loadingFilterElement = true;
    const tokenData = await this.getToken(this.workspaceId, this.reportId);
    if (tokenData) {
      tokenData.page = '';
      this.eventOpenmodal.next(tokenData);
      if (this.delivery.filters && this.delivery.filters !== '[]' && this.delivery.filters !== '{}') {
        this.eventUpdateFilter.next(JSON.parse(this.delivery.filters));
      }
    }
    this.loadingFilterElement = false;
  }

  onResetFilterClick() {
    this.delivery.filters = '[]';
    this.filterAppliedCount = undefined;
    this.deliveryForm.get('Filters').setValue('[]');
  }

  onSubmitFilter(filterObject) {
    const filterString = JSON.stringify(filterObject);
    this.delivery.filters = filterString;
    this.deliveryForm.get('Filters').setValue(filterString);
    const activeFilters = filterObject.filter(filter => filter && (typeof filter.operator !== 'string' || filter.operator !== 'All'));
    if (filterString && activeFilters.length > 0) {
      this.filterAppliedCount = activeFilters.length;
    } else {
      this.filterAppliedCount = undefined;
    }
  }

}
